import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { PaperPlaneRight, Paperclip, Microphone, MicrophoneSlash, Image, Lightning, ChatCircle, X, Brain, Sparkle, CreditCard, LockSimple, CaretDown, Check } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { LOCAL_MODELS } from '@/lib/localEngine';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatInput({ onSend, onImage, onFile, onMultimodal, agentMode, onToggleAgent, loading, inputRef, templateText, onTemplateUsed, mode, onToggleMode, credits, onBuyCredits, localTier, onTierChange, heavyMode, onToggleHeavy, onManageModels }) {
  const [msg, setMsg] = useState('');
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [imgMode, setImgMode] = useState(false);
  const [attachedImage, setAttachedImage] = useState(null); // { file, preview }
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyAmount, setBuyAmount] = useState(1);
  const [buyStep, setBuyStep] = useState('select'); // 'select', 'pay', 'confirm', 'done'
  const [cashAppName, setCashAppName] = useState('');
  const [buyResult, setBuyResult] = useState(null);
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const recRef = useRef(null);

  useEffect(() => {
    if (taRef.current) {
      taRef.current.style.height = 'auto';
      taRef.current.style.height = Math.min(taRef.current.scrollHeight, 160) + 'px';
    }
  }, [msg]);

  // Handle template text injection
  useEffect(() => {
    if (templateText) {
      setMsg(templateText);
      onTemplateUsed?.();
      setTimeout(() => taRef.current?.focus(), 50);
    }
  }, [templateText, onTemplateUsed]);

  // Handle paste images
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const preview = URL.createObjectURL(file);
            setAttachedImage({ file, preview });
            setImgMode(false);
          }
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const submit = () => {
    if (loading) return;

    if (attachedImage) {
      // Multimodal: image + text
      onMultimodal?.(msg.trim() || 'Describe this image', attachedImage.file);
      setAttachedImage(null);
      setMsg('');
      return;
    }

    if (!msg.trim()) return;
    if (imgMode) { onImage(msg.trim()); setImgMode(false); }
    else onSend(msg.trim());
    setMsg('');
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { onFile(f); e.target.value = ''; }
  };

  const onImageAttach = (e) => {
    const f = e.target.files?.[0];
    if (f && f.type.startsWith('image/')) {
      const preview = URL.createObjectURL(f);
      setAttachedImage({ file: f, preview });
      setImgMode(false);
    }
    e.target.value = '';
  };

  const removeImage = () => {
    if (attachedImage?.preview) URL.revokeObjectURL(attachedImage.preview);
    setAttachedImage(null);
  };

  const startBrowserSR = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.continuous = false; r.interimResults = true; r.lang = 'en-US';
    r.onresult = (ev) => { let t = ''; for (let i = 0; i < ev.results.length; i++) t += ev.results[i][0].transcript; setMsg(t); };
    r.onend = () => setRecording(false);
    r.onerror = () => setRecording(false);
    recRef.current = r; r.start(); setRecording(true);
  };

  // Voice input: MediaRecorder → Whisper transcription, browser SpeechRecognition fallback
  const toggleVoice = async () => {
    if (recording) { recRef.current?.stop(); setRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        if (blob.size < 1000) return;
        const fd = new FormData();
        fd.append('file', blob, 'voice.webm');
        setTranscribing(true);
        try {
          const { data } = await axios.post(`${API}/voice/transcribe`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          if (data.text) setMsg(m => (m ? `${m} ` : '') + data.text.trim());
        } catch (err) { console.error('Transcription failed', err); }
        finally { setTranscribing(false); }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch {
      startBrowserSR();
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div data-testid="chat-input-container">
        <div className={`input-elevated rounded-2xl border bg-white/5 transition-all ${agentMode ? 'border-lime-400/40 shadow-lime-500/10' : 'border-white/10'}`}>
          {/* Mode bar */}
          <div className="flex items-center gap-2 px-4 pt-3">
            {/* Agent toggle */}
            <button onClick={onToggleAgent} data-testid="agent-mode-toggle"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide transition-all ${
                agentMode ? 'bg-lime-400/15 text-lime-300 border border-lime-400/25' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/25'
              }`}>
              {agentMode ? <Lightning weight="fill" className="w-3 h-3" /> : <ChatCircle className="w-3 h-3" />}
              {agentMode ? 'Agent' : 'Chat'}
            </button>

            {/* Model toggle */}
            <button onClick={onToggleMode} data-testid="model-mode-toggle"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide transition-all ${
                mode === 'thinking'
                  ? 'bg-violet-400/15 text-violet-300 border border-violet-400/25'
                  : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/25'
              }`}>
              {mode === 'thinking' ? <Brain weight="fill" className="w-3 h-3" /> : <Sparkle weight="fill" className="w-3 h-3" />}
              {mode === 'thinking' ? 'Thinking' : 'Standard'}
            </button>

            {/* On-device model tier selector */}
            {localTier && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="local-tier-selector"
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide bg-emerald-400/10 text-emerald-300 border border-emerald-400/25 hover:bg-emerald-400/20 transition-all">
                    <LockSimple weight="fill" className="w-3 h-3" />
                    {LOCAL_MODELS[localTier]?.label?.replace('Lindey ', '') || 'Pro'}
                    <CaretDown className="w-2.5 h-2.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 rounded-xl">
                  <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40">On-device model</div>
                  {Object.entries(LOCAL_MODELS).map(([key, m]) => (
                    <DropdownMenuItem key={key} onClick={() => onTierChange?.(key)} data-testid={`local-tier-${key}`} className="rounded-lg cursor-pointer">
                      <div className="flex items-start gap-2 w-full">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-medium text-white/90">{m.label}</span>
                            <span className="text-[10px] font-mono text-white/40">{m.size}</span>
                          </div>
                          <p className="text-[11px] text-white/40">{m.desc}</p>
                        </div>
                        {localTier === key && <Check weight="bold" className="w-3.5 h-3.5 text-emerald-400 mt-1" />}
                      </div>
                    </DropdownMenuItem>
                  ))}
                  {onManageModels && (
                    <DropdownMenuItem onClick={onManageModels} data-testid="manage-models-button" className="rounded-lg cursor-pointer border-t border-white/10 mt-1 pt-2">
                      <span className="text-[12px] font-medium text-lime-300">Manage on-device models…</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Lindey Heavy toggle */}
            {onToggleHeavy && (
              <button onClick={onToggleHeavy} data-testid="heavy-mode-toggle"
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide transition-all border ${
                  heavyMode ? 'heavy-chip-active' : 'bg-white/5 text-white/50 border-white/10 hover:border-lime-400/40 hover:text-lime-300'
                }`}>
                <Lightning weight="fill" className="w-3 h-3" />
                Heavy
              </button>
            )}

            {/* Credits badge */}
            {credits && !credits.is_unlimited && (
              <div className="flex items-center gap-1.5">
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                  credits.thinking_credits > 5 ? 'bg-white/10 text-white/50' : credits.thinking_credits > 0 ? 'bg-amber-400/10 text-amber-300' : 'bg-red-400/10 text-red-400'
                }`} data-testid="thinking-credits-badge">
                  {credits.thinking_credits} thinking left
                </span>
                {credits.thinking_credits <= 5 && (
                  <button onClick={() => setShowBuyModal(true)} data-testid="buy-credits-button"
                    className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-lime-400/15 text-lime-300 hover:bg-lime-400/25 transition-colors">
                    <CreditCard className="w-3 h-3" /> Buy
                  </button>
                )}
              </div>
            )}
            {credits?.is_unlimited && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-violet-400/10 text-violet-300" data-testid="unlimited-badge">
                Unlimited
              </span>
            )}

            {imgMode && (
              <span className="flex items-center gap-1 text-[11px] font-medium text-lime-400 ml-auto">
                <Image className="w-3 h-3" weight="fill" /> Image gen
              </span>
            )}
          </div>

          {/* Attached image preview */}
          {attachedImage && (
            <div className="px-4 pt-2">
              <div className="relative inline-block">
                <img src={attachedImage.preview} alt="Attached" className="h-20 rounded-lg border border-white/10 object-cover" />
                <button onClick={removeImage} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-black transition-colors" data-testid="remove-attached-image">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* Textarea */}
          <div className="px-4 py-2">
            <textarea ref={(el) => { taRef.current = el; if (inputRef) inputRef.current = el; }} value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={onKey}
              placeholder={attachedImage ? 'Ask about this image...' : imgMode ? 'Describe the image...' : agentMode ? 'Describe a complex task...' : 'Message Lindey...'}
              rows={1} disabled={loading}
              className="w-full resize-none bg-transparent border-0 outline-none text-sm text-white/90 placeholder:text-white/30 leading-relaxed"
              style={{ fontFamily: 'Satoshi, sans-serif' }}
              data-testid="chat-message-input" />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-0.5">
              <input type="file" ref={fileRef} onChange={onFileChange} className="hidden" accept=".pdf,.doc,.docx,.txt,.csv" data-testid="file-input" />
              <input type="file" ref={imgRef} onChange={onImageAttach} className="hidden" accept="image/*" data-testid="image-attach-input" />

              <Tooltip><TooltipTrigger asChild>
                <button onClick={() => fileRef.current?.click()} disabled={loading} data-testid="file-upload-button"
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/10 transition-all">
                  <Paperclip className="w-4 h-4" />
                </button>
              </TooltipTrigger><TooltipContent className="text-xs">Attach file</TooltipContent></Tooltip>

              <Tooltip><TooltipTrigger asChild>
                <button onClick={() => imgRef.current?.click()} disabled={loading} data-testid="image-attach-button"
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${attachedImage ? 'text-lime-400 bg-lime-400/10' : 'text-white/40 hover:text-white/80 hover:bg-white/10'}`}>
                  <Image className="w-4 h-4" weight={attachedImage ? 'fill' : 'regular'} />
                </button>
              </TooltipTrigger><TooltipContent className="text-xs">{attachedImage ? 'Image attached' : 'Attach image'}</TooltipContent></Tooltip>

              <Tooltip><TooltipTrigger asChild>
                <button onClick={toggleVoice} disabled={loading} data-testid="voice-input-button"
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${recording ? 'text-red-400 bg-red-400/10 animate-pulse' : 'text-white/40 hover:text-white/80 hover:bg-white/10'}`}>
                  {transcribing ? <span className="w-3.5 h-3.5 border-2 border-white/20 border-t-lime-400 rounded-full animate-spin" /> : recording ? <MicrophoneSlash className="w-4 h-4" /> : <Microphone className="w-4 h-4" />}
                </button>
              </TooltipTrigger><TooltipContent className="text-xs">{recording ? 'Stop' : 'Voice'}</TooltipContent></Tooltip>
            </div>

            <button onClick={submit} disabled={(!msg.trim() && !attachedImage) || loading} data-testid="chat-submit-button"
              className="w-8 h-8 rounded-lg bg-lime-400/100 hover:bg-lime-600 flex items-center justify-center text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
              <PaperPlaneRight weight="fill" className="w-4 h-4" />
            </button>
          </div>
        </div>
        <p className="text-[10px] text-white/40 text-center mt-2">Lindey can make mistakes. Paste images or attach files to chat about them.</p>

        {/* Buy Credits Modal */}
        <Dialog open={showBuyModal} onOpenChange={(open) => { setShowBuyModal(open); if (!open) { setBuyStep('select'); setCashAppName(''); setBuyResult(null); } }}>
          <DialogContent className="sm:max-w-[400px]" data-testid="buy-credits-modal">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {buyStep === 'done' ? 'Purchase Submitted' : 'Buy Thinking Credits'}
              </DialogTitle>
              <DialogDescription className="text-sm text-white/50">
                {buyStep === 'select' && 'Power up Lindey Thinking with Claude for deeper reasoning.'}
                {buyStep === 'pay' && 'Send payment via Cash App, then confirm below.'}
                {buyStep === 'confirm' && 'Enter your Cash App name so we can verify your payment.'}
                {buyStep === 'done' && 'Your credits will be added once payment is verified.'}
              </DialogDescription>
            </DialogHeader>

            {buyStep === 'select' && (
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-3 gap-2">
                  {[1, 5, 10].map(amt => (
                    <button key={amt} onClick={() => setBuyAmount(amt)} data-testid={`buy-amount-${amt}`}
                      className={`py-3 rounded-xl text-center transition-all border ${
                        buyAmount === amt
                          ? 'border-lime-400 bg-lime-400/10 ring-1 ring-lime-400/40'
                          : 'border-white/10 hover:border-white/25 bg-white/5'
                      }`}>
                      <div className="text-lg font-bold text-white">${amt}</div>
                      <div className="text-[11px] text-white/50">{amt * 50} credits</div>
                    </button>
                  ))}
                </div>
                <div className="text-xs text-white/40 text-center">1 credit = 1 Thinking message</div>
                <button onClick={() => setBuyStep('pay')} data-testid="proceed-to-pay"
                  className="w-full py-2.5 rounded-xl bg-lime-400/100 hover:bg-lime-600 text-white font-semibold text-sm transition-colors">
                  Continue — ${buyAmount}
                </button>
              </div>
            )}

            {buyStep === 'pay' && (
              <div className="space-y-4 pt-2">
                <div className="bg-white/5 rounded-xl p-4 text-center space-y-2">
                  <div className="text-2xl font-bold text-white">${buyAmount}.00</div>
                  <div className="text-sm text-white/50">Send to <span className="font-semibold text-white/90">$mrwyattedwards</span> on Cash App</div>
                </div>
                <a href={`https://cash.app/$mrwyattedwards/${buyAmount}`} target="_blank" rel="noopener noreferrer" data-testid="open-cashapp-link"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-[#00D632] hover:brightness-95 text-white font-semibold text-sm transition-all">
                  Open Cash App
                </a>
                <button onClick={() => setBuyStep('confirm')} data-testid="ive-paid-button"
                  className="w-full py-2.5 rounded-xl border border-white/10 hover:border-white/25 text-white/80 font-semibold text-sm transition-colors">
                  I've sent the payment
                </button>
                <button onClick={() => setBuyStep('select')} className="w-full text-xs text-white/40 hover:text-white/80 transition-colors">
                  Go back
                </button>
              </div>
            )}

            {buyStep === 'confirm' && (
              <div className="space-y-4 pt-2">
                <div className="text-sm text-white/70">Enter your Cash App name or $cashtag so we can match your payment.</div>
                <input type="text" value={cashAppName} onChange={e => setCashAppName(e.target.value)}
                  placeholder="e.g. $johndoe or John Doe"
                  className="w-full px-3 py-2.5 rounded-xl border border-white/10 text-sm focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400/40"
                  data-testid="cashapp-name-input" />
                <button onClick={() => { onBuyCredits?.(buyAmount, cashAppName); setBuyStep('done'); }} disabled={!cashAppName.trim()} data-testid="confirm-purchase-button"
                  className="w-full py-2.5 rounded-xl bg-lime-400/100 hover:bg-lime-600 text-white font-semibold text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  Submit for verification
                </button>
                <button onClick={() => setBuyStep('pay')} className="w-full text-xs text-white/40 hover:text-white/80 transition-colors">
                  Go back
                </button>
              </div>
            )}

            {buyStep === 'done' && (
              <div className="space-y-4 pt-2 text-center">
                <div className="w-12 h-12 rounded-full bg-lime-400/15 flex items-center justify-center mx-auto">
                  <Sparkle weight="fill" className="w-6 h-6 text-lime-400" />
                </div>
                <div className="text-sm text-white/70">Your purchase of <span className="font-semibold">{buyAmount * 50} credits</span> for <span className="font-semibold">${buyAmount}</span> has been submitted. Credits will appear once verified — usually within a few hours.</div>
                <button onClick={() => { setShowBuyModal(false); setBuyStep('select'); setCashAppName(''); }} data-testid="close-buy-modal"
                  className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white/80 font-semibold text-sm transition-colors">
                  Done
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
